import React, { useState } from "react";
import Icon from "@mdi/react";
import { mdiChevronDown, mdiChevronUp } from "@mdi/js";
import CustomFormInput from "./forms/CustomFormInput";
import BaseModal from "./BaseModal";
import "./SupplierPolicyAcknowledgement.css";

/**
 * Buyer-facing ordered labels for the 7 resolved policy fields (privateLessons /
 * classEnrolments). Labels are written from the buyer's perspective (so the
 * customer-cancellation field is just "Cancellation", and the supplier-side field is
 * "If the provider cancels").
 */
export const SUPPLIER_POLICY_SECTIONS = [
    { key: "cancellation", label: "Cancellation" },
    { key: "noShow", label: "No-show" },
    { key: "lateArrival", label: "Late arrival" },
    { key: "rescheduling", label: "Rescheduling" },
    { key: "refundCredit", label: "Refunds and credits" },
    { key: "supplierCancellation", label: "If the provider cancels" },
    { key: "taxInvoiceContact", label: "Tax invoices" },
];

/**
 * Supplier-facing editor field labels (Settings + instructor profile). Written from
 * the supplier's editing perspective, so the two cancellation fields are disambiguated
 * ("by the customer" vs "by you"). Shared by both editors so they stay in lockstep.
 */
export const SUPPLIER_POLICY_EDITOR_FIELDS = [
    { key: "cancellation", label: "Cancellation by the customer" },
    { key: "noShow", label: "No-show" },
    { key: "lateArrival", label: "Late arrival" },
    { key: "rescheduling", label: "Rescheduling" },
    { key: "refundCredit", label: "Refunds and credits" },
    { key: "supplierCancellation", label: "Cancellation by you" },
    { key: "taxInvoiceContact", label: "Tax invoice contact" },
];

/**
 * Map a resolved policy object (7 string fields) into the `sections` shape the gate
 * renders. Empty fields are kept here and filtered for display inside the component.
 * @param {object|null} policy
 * @returns {{label: string, text: string}[]}
 */
export function policyToSections(policy) {
    if (!policy) return [];
    return SUPPLIER_POLICY_SECTIONS.map(({ key, label }) => ({ label, text: policy[key] || "" }));
}

/**
 * Buyer-facing Supplier policy acknowledgement gate. Shown on a checkout page above
 * the Pay action. Displays the Supplier's policy sections, disclosed-agent wording
 * (the Supplier owns the policy; Dance Localé is the agent that collects payment on
 * the Supplier's behalf), and an "I agree" checkbox. The host page gates its Pay
 * action on `acknowledged`. The gate is UX only — no acknowledgement is persisted.
 *
 * @param {{label: string, text: string}[]} sections - policy rows; rows with empty text are skipped
 * @param {string} supplierName - the Supplier (instructor / studio / organiser) name
 * @param {string} surfaceLabel - "lesson" | "class enrolment" | "ticket"
 * @param {boolean} acknowledged
 * @param {(checked: boolean) => void} onAcknowledgedChange
 * @param {"accordion"|"modal-link"} [variant="accordion"] - "accordion" (default) shows the
 *   collapsible click-to-read chevron row used by booking/class checkout. "modal-link" (ticket
 *   checkout) drops the chevron row and instead turns "{name}'s terms and conditions" inside the
 *   checkbox label into an inline link that opens the terms in a modal.
 */
const SupplierPolicyAcknowledgement = ({
    sections = [],
    supplierName,
    surfaceLabel,
    acknowledged,
    onAcknowledgedChange,
    variant = "accordion",
}) => {
    const [expanded, setExpanded] = useState(false);
    const [showTermsModal, setShowTermsModal] = useState(false);
    const name = supplierName || "the supplier";
    const visibleSections = (sections || []).filter(s => s && s.text && s.text.trim() !== "");

    const sellerDisclosure = (
        <p className="supplier-policy-ack-disclosure">
            {name} is the seller of this {surfaceLabel}. Dance Localé collects payment on their behalf and is
            not the seller.
        </p>
    );

    const dlTermsLink = (
        <a
            className="supplier-policy-ack-link"
            href="/terms-conditions"
            target="_blank"
            rel="noopener noreferrer"
            onClick={e => e.stopPropagation()}
        >
            the Dance Localé Terms of Service
        </a>
    );

    // Ticket checkout: no accordion. The school's terms open in a modal from an inline link
    // inside the checkbox label; the seller-of-record line sits above the checkbox.
    if (variant === "modal-link") {
        return (
            <div className="supplier-policy-ack supplier-policy-ack--modal-link">
                {sellerDisclosure}

                <CustomFormInput
                    type="checkbox"
                    variant="purple"
                    label={
                        <>
                            I have read and agree to the{" "}
                            <button
                                type="button"
                                className="supplier-policy-ack-link supplier-policy-ack-link-btn"
                                onClick={e => { e.stopPropagation(); setShowTermsModal(true); }}
                            >
                                {name} Terms
                            </button>
                            {" "}and {dlTermsLink}.
                        </>
                    }
                    checked={acknowledged}
                    onChange={e => onAcknowledgedChange(e.target.checked)}
                />

                <BaseModal
                    isOpen={showTermsModal}
                    onClose={() => setShowTermsModal(false)}
                    title={`${name}'s Terms and Conditions`}
                    footer={
                        <button
                            type="button"
                            className="base-modal-cancel-btn"
                            onClick={() => setShowTermsModal(false)}
                        >
                            Close
                        </button>
                    }
                >
                    {/* A published ticket always has terms stored — the wizard requires the
                        field and pre-fills it from the org/platform default — so no empty-state
                        branch is needed here. */}
                    <div className="supplier-policy-ack-sections">
                        {visibleSections.map((s, i) => (
                            <div className="supplier-policy-ack-row" key={i}>
                                <span className="supplier-policy-ack-label">{s.label}</span>
                                <p className="supplier-policy-ack-text">{s.text}</p>
                            </div>
                        ))}
                    </div>
                </BaseModal>
            </div>
        );
    }

    return (
        <div className="supplier-policy-ack">
            {/* Click-to-read accordion — terms stay collapsed so the checkout isn't a wall of text. */}
            <button
                type="button"
                className="supplier-policy-ack-toggle"
                aria-expanded={expanded}
                onClick={() => setExpanded(prev => !prev)}
            >
                <span>Read {name}'s terms and conditions</span>
                <Icon path={expanded ? mdiChevronUp : mdiChevronDown} size={0.85} />
            </button>

            {expanded && (
                visibleSections.length > 0 ? (
                    <div className="supplier-policy-ack-sections">
                        {visibleSections.map((s, i) => (
                            <div className="supplier-policy-ack-row" key={i}>
                                <span className="supplier-policy-ack-label">{s.label}</span>
                                <p className="supplier-policy-ack-text">{s.text}</p>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="supplier-policy-ack-text supplier-policy-ack-empty">
                        {name} has not added detailed terms. By continuing you agree to {name}'s terms for this {surfaceLabel}.
                    </p>
                )
            )}

            {sellerDisclosure}

            <CustomFormInput
                type="checkbox"
                variant="purple"
                label={
                    <>
                        I have read and agree to {name}'s terms and conditions and{" "}
                        {dlTermsLink}.
                    </>
                }
                checked={acknowledged}
                onChange={e => onAcknowledgedChange(e.target.checked)}
            />
        </div>
    );
};

export default SupplierPolicyAcknowledgement;
